import { Box, Spinner, Td, Tr } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import {
  Table as CTable,
  Tbody,
  TableContainer,
} from '@chakra-ui/react';
import { MdOutlineFindInPage } from 'react-icons/md';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

import { TableBaseProps, TableFilter } from './types';
import { TableHeader } from './components/table-header';
import { TableColumns } from './components/table-columns';
import { TablePagination } from './components/table-pagination';
import { TableFilterDialog } from './components/table-filter';
import { Txt } from '../text';
import { DraggableTableRow } from './components/draggable-table-row';

export function DraggableTableBase<I extends EntityBase = EntityBase>(props: TableBaseProps<I> & { 
  sortProperty?: string,
  onDragEnd?: (updatedArray: I[], oldIndex: number, newIndex: number) => void
}) {
  const {
    title,
    columns,
    createMenu,
    onCreateClick,
    onDownload,
    total,
    keyProperty = 'id',
    sortProperty = 'value',
    initialShowFilter,
    data,
    pagination,
    filter,
    sort,
    selectedRow,
    onRowClick,
    onSortChange,
    onFilterChange,
    onPaginationChange,
    loading,
    paper = true,
    disabled,
    additionalToolbar,
    secondaryToolbar,
    footerAdditional,
    onDragEnd,
    getRowBackgroundColor,
  } = props;
  const [showFilter, setShowFilter] = useState(initialShowFilter || false);

  const onOpenFilter = useCallback(() => {
    setShowFilter(true);
  }, []);

  const needFilter = useMemo(() => {
    return columns.some((column) => column.options.filter);
  }, [columns]);

  const hasGroups = useMemo(() => {
    return columns.some((column) => !!column.options.group);
  }, [columns]);

  const onPageChange = useCallback((page: number) => {
    onPaginationChange({
      page,
      pageSize: pagination.pageSize,
    });
  }, [onPaginationChange, pagination.pageSize]);

  const onPageSizeChange = useCallback((pageSize: number) => {
    onPaginationChange({
      page: 1,
      pageSize,
    });
  }, [onPaginationChange]);

  const onFilterSubmit = useCallback((filter: TableFilter[]) => {
    onFilterChange(filter);
    setShowFilter(false);
  }, [onFilterChange]);

  const visibleColumns = useMemo(() => {
    const sortableColumn = {
      name: sortProperty,
      label: '',
      options: {
        sort: false,
      },
    };
    return [ sortableColumn, ...columns.filter((column) => !column.options?.hidden) ]; 
  }, [columns, sortProperty]);

  // isolate
  const TableColumnsComp = useMemo(() => <TableColumns<I> 
    sort={sort} columns={visibleColumns} onSortChange={onSortChange} grouped={hasGroups}
  />, [onSortChange, sort, visibleColumns, hasGroups]);

  // drag and 
  const [draggingId, setDraggingId] = useState();
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  function handleDragStart(event) {
    setDraggingId(event.active.id);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const ids = data.map(({ id }) => id);
      const oldIndex = ids.indexOf(active.id);
      const newIndex = ids.indexOf(over.id);
      onDragEnd?.(arrayMove(data, oldIndex, newIndex), oldIndex, newIndex);
    }

    setDraggingId(null);
  }

  function handleDragCancel() {
    setDraggingId(null);
  }

  const draggingRow = useMemo(() => {
    if (!draggingId) {
      return null;
    }
    const row = data.find(({ id }) => id === draggingId);
    return row;
  }, [draggingId, data]);

  const TableBody = useMemo(() => {
    return <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
    >
      <Tbody>
        {data.length === 0 && <Tr>
          <Td colSpan={visibleColumns.length}>
            <Box minH={200} display='flex' gap={4} alignItems='center' justifyContent='center'>
              {!loading && <><MdOutlineFindInPage size={32} color='var(--chakra-colors-gray-400' />
                <Txt color='gray.500'>Пусто</Txt></>}
            </Box>
          </Td>
        </Tr>}
        <SortableContext items={data.map(({ id }) => id)} strategy={verticalListSortingStrategy}>
        {data.map((row: I, rowIndex) => {
          return <DraggableTableRow<I>
            key={`${row[keyProperty]}_${rowIndex}`}
            rowIndex={rowIndex}
            columns={visibleColumns}
            rowData={row}
            loading={loading} 
            selectedRow={selectedRow}
            onRowClick={onRowClick}
            totalRows={data.length}
            getRowBackgroundColor={getRowBackgroundColor}
          />;
        })}
        </SortableContext>
      </Tbody>
      <DragOverlay>
        {draggingId && (
          <CTable variant='simple' style={{ width: '100%', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.25)' }}>
            <Tbody>
              <DraggableTableRow<I> rowData={draggingRow} columns={visibleColumns} rowIndex={0} totalRows={data.length} />
            </Tbody>
          </CTable>
        )}
      </DragOverlay>
    </DndContext>;
  }, 
  [data, getRowBackgroundColor, keyProperty, loading, onRowClick, selectedRow, visibleColumns, hasGroups, sensors, handleDragStart, handleDragEnd, handleDragCancel]);

  return <TableContainer 
    backgroundColor='white'
    borderRadius={paper ? 'md' : 'none'}
    boxShadow={paper ? 'md' : 'none'}
    position='relative'
  >
    <TableHeader 
      disabled={disabled}
      title={title}
      createMenu={createMenu}
      onFilterClick={needFilter ? onOpenFilter : undefined}
      onDownloadClick={onDownload}
      onCreateClick={onCreateClick}
      additionalToolbar={additionalToolbar}
      secondaryToolbar={secondaryToolbar}
    />
    <Box overflowX='auto'>
      <CTable variant='simple'>
        {TableColumnsComp }
        {TableBody}
      </CTable>

    </Box>
    <TablePagination 
      page={pagination.page || 1}
      pageSize={pagination.pageSize}
      total={total}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      footerAdditional={footerAdditional}
    />
    {loading && <Box 
      position='absolute' backgroundColor='whiteAlpha.500' top={0} left={0} right={0} bottom={0}
      display='flex' alignItems='center' justifyContent='center'
    >
      <Spinner  />
    </Box>}
    <TableFilterDialog<I>
      title={title}
      isOpen={showFilter} 
      onClose={() => setShowFilter(false)}
      columns={columns}
      filters={filter}
      onFilterSubmit={onFilterSubmit}
    /> 
  </TableContainer>;
}